import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector
} from '@mui/x-data-grid';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { ChangeEvent, FC, memo, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import { queryKey } from 'src/hooks/useOrders.request';
import { saveAs } from 'src/utils/fileSaver';

import MyGridToolbarFilterButton from '../common/MyGridToolbarFilterButton';

const CustomToolbar: FC<{
  startOn: Date;
  endOn: Date;
  removeSelectedOrders: () => Promise<void>;
  resetSelectedOrders: () => Promise<void>;
  toggleDialogIsOpen: () => void;
  setIsLoading: (prevState: boolean) => boolean;
  isLoading: boolean;
  handleDateRange: (range: [Date, Date]) => void;
  canDeleteAll: boolean;
  deleteAll: (settledFnk: () => void) => void;
  handleDownloadExcel: () => void;
  isPlanningView: boolean;
  setFetchPage: (i: number) => void;
  onClickDownloadOutsourcingPdf: () => void;
}> = memo(
  ({
    startOn,
    endOn,
    removeSelectedOrders,
    resetSelectedOrders,
    toggleDialogIsOpen,
    setIsLoading,
    isLoading,
    handleDateRange,
    canDeleteAll,
    deleteAll,
    handleDownloadExcel,
    isPlanningView,
    setFetchPage,
    onClickDownloadOutsourcingPdf,
  }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const licenseContext = useContext(LicenseContext);

    const queryClient = useQueryClient();
    const [canUseUpload, setCanUseUpload] = useState<boolean>(false);
    const [deleteHandlerDialogIsOpen, setDeleteHandlerDialogIsOpen] = useState<boolean>(false);
    const [useOutsourcingContractPdf, setUseOutsourcingContractPdf] = useState(false);

    const deleteHandlerDialogOnClose = () => {
      setDeleteHandlerDialogIsOpen(false);
    };

    const deleteBtnOnClick = () => {
      if (canDeleteAll) {
        setDeleteHandlerDialogIsOpen(true);

        return;
      }

      void removeSelectedOrders().finally(() => {
        setDeleteHandlerDialogIsOpen(false);
        setFetchPage(0);
      });
    };

    useEffect(() => {
      if (!licenseContext.config) return;

      setCanUseUpload(licenseContext.config.selected_company_id !== 0);
      setUseOutsourcingContractPdf(licenseContext.config.use_outsourcing_contract_pdf);
    }, [licenseContext.config]);

    const [importFile, setImportFile] = useState<File | undefined>(undefined);
    const importButtonOnClick = (event: ChangeEvent<HTMLInputElement>) => {
      setImportFile(event.target.files[0]);
    };

    const [importForUpdateFile, setImportForUpdateFile] = useState<File | undefined>(undefined);
    const importForUpdateButtonOnClick = (event: ChangeEvent<HTMLInputElement>) => {
      setImportForUpdateFile(event.target.files[0]);
    };

    useEffect(() => {
      if (!importFile) return;

      const requestPath = '/api/v2/orders/import';

      const data = new FormData();
      data.append('file', importFile);

      setIsLoading(true);
      enqueueSnackbar('インポートを開始します');

      axios
        .post(requestPath, data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(
          (response: AxiosResponse<{ severity: string; message: string }>) => {
            enqueueSnackbar(response.data.message);
          }
        )
        .catch((e) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (e.response.status === 400) {
            enqueueSnackbar(
              '取り込めない注文がありました、データをご確認ください。'
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
            const blob = new Blob([e.response.data], {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
              type: e.response.data.type
            });
            saveAs(blob, 'errors.txt');
          } else {
            enqueueSnackbar(fiveZeroZeroErrorMessage);
          }
        })
        .finally(() => {
          setFetchPage(0);
          setImportFile(undefined);
          setIsLoading(false);
          void queryClient.invalidateQueries({
            queryKey: ['orders']
          });
          void queryClient.invalidateQueries({
            queryKey: [queryKey]
          });
          selectImportMethodDialogOnClose();
        });
    }, [enqueueSnackbar, importFile]);

    useEffect(() => {
      if (!importForUpdateFile) return;

      const requestPath = '/api/v3/orders/import_for_update';

      const data = new FormData();
      data.append('file', importForUpdateFile);

      setIsLoading(true);
      enqueueSnackbar('インポートを開始します');

      axios.post(requestPath, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(
        (response: AxiosResponse<{ severity: string; message: string, warnings: string[] }>) => {
          enqueueSnackbar(response.data.message);
          setFetchPage(0);
          if (response.data.warnings.length > 0) {
            enqueueSnackbar(response.data.warnings.join('\n'), { style: { whiteSpace: 'pre-line' } });
          }
        }
      ).catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response.status === 400) {
          enqueueSnackbar(
            '取り込めない注文がありました、データをご確認ください。'
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
          const blob = new Blob([e.response.data], {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
            type: e.response.data.type
          });
          saveAs(blob, 'errors.txt');
        } else {
          enqueueSnackbar(fiveZeroZeroErrorMessage);
        }
      })
      .finally(() => {
        setImportForUpdateFile(undefined);
        setIsLoading(false);
        setFetchPage(0);
        void queryClient.invalidateQueries({
          queryKey: ['orders']
        });
        void queryClient.resetQueries({
          queryKey: [queryKey]
        });
        selectImportMethodDialogOnClose();
      });
    }, [enqueueSnackbar, importForUpdateFile, queryClient, setIsLoading, setFetchPage]);

    const [selectImportMethodDialogIsOpen, setSelectImportMethodDialogIsOpen] = useState<boolean>(false);
    const selectImportMethodButtonOnClick = () => {
      setSelectImportMethodDialogIsOpen(true);
    };
    const selectImportMethodDialogOnClose = () => {
      setSelectImportMethodDialogIsOpen(false);
    };

    const customImportButtonOnClick = () => {
      void navigate('/import');
      setFetchPage(0);
    };

    return (
      <>
        <GridToolbarContainer
          sx={{
            width: '100%'
          }}
        >
          <Dialog
            open={selectImportMethodDialogIsOpen}
            onClose={selectImportMethodDialogOnClose}
            fullWidth
            maxWidth="md"
            // maxWidth="sm"
          >
            <DialogTitle>インポート形式を選択してください</DialogTitle>
            <DialogContent>
              <Stack
                direction="row"
                gap={1}
              >
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  loading={isLoading}
                  disabled={isLoading || !canUseUpload}
                >
                  標準ファイルアップロード
                  <input
                    type="file"
                    disabled={isLoading || !canUseUpload}
                    onChange={importButtonOnClick}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{
                      opacity: 0,
                      appearance: 'none',
                      position: 'absolute',
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  />
                </LoadingButton>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  loading={isLoading}
                  onClick={customImportButtonOnClick}
                >
                  カスタムフォーマット
                </LoadingButton>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  loading={isLoading}
                  disabled={isLoading || !canUseUpload}
                >
                  上書きアップロード (エクスポートしたファイルを利用してください)
                  <input
                    type="file"
                    disabled={isLoading || !canUseUpload}
                    onChange={importForUpdateButtonOnClick}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{
                      opacity: 0,
                      appearance: 'none',
                      position: 'absolute',
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  />
                </LoadingButton>
              </Stack>
            </DialogContent>
          </Dialog>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'end',
              width: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start'
              }}
              flexGrow={1}
            >
              {!isPlanningView && (
                <Stack
                  direction="row"
                  gap={0.3}
                >
                  <DateRangePicker
                    value={[
                      startOn,
                      endOn
                    ]}
                    onChange={handleDateRange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { maxWidth: '120px' },
                      }
                    }}
                  />
                </Stack>
              )}
              <GridToolbarColumnsButton />
              <MyGridToolbarFilterButton />
              <GridToolbarDensitySelector />
              <Button
                startIcon={<CloudUploadRoundedIcon />}
                sx={{
                  backgroundColor: '#fff'
                }}
                onClick={selectImportMethodButtonOnClick}
              >
                インポート
              </Button>
              <Button
                startIcon={<SystemUpdateAltRoundedIcon />}
                onClick={handleDownloadExcel}
                sx={{ textTransform: 'none' }}
              >
                エクスポート
              </Button>
              <Button
                startIcon={<AddCircleOutlinedIcon />}
                onClick={toggleDialogIsOpen}
              >
                追加・修正
              </Button>
              <Button
                onClick={deleteBtnOnClick}
                startIcon={<DeleteForeverOutlinedIcon />}
              >
                削除
              </Button>
              <Button
                onClick={() => {
                  (async () => {
                    await resetSelectedOrders();
                  })().catch((e) => {
                    console.log(e);
                  });
                }}
                startIcon={<RotateLeftRoundedIcon />}
              >
                割当解除
              </Button>
              {useOutsourcingContractPdf && (
                <Button
                  onClick={onClickDownloadOutsourcingPdf}
                  startIcon={<ArticleIcon />}
                >
                  委託表出力
                </Button>
              )}
            </Box>
          </Box>
        </GridToolbarContainer>
        <Dialog
          open={deleteHandlerDialogIsOpen}
          onClose={deleteHandlerDialogOnClose}
        >
          <DialogTitle>
            削除する対象を選択してください
          </DialogTitle>
          <DialogActions>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteForeverRoundedIcon />}
              onClick={() => {
                deleteAll(deleteHandlerDialogOnClose);
              }}
            >
              すべて削除する
            </Button>
            <Button
              onClick={() => {
                void removeSelectedOrders().finally(() => {
                  setDeleteHandlerDialogIsOpen(false);
                });
              }}
              variant="outlined"
              startIcon={<DeleteSweepRoundedIcon />}
            >
              選択したものを削除する
            </Button>
            <Button
              onClick={deleteHandlerDialogOnClose}
              startIcon={<HighlightOffRoundedIcon />}
            >
              キャンセル
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default CustomToolbar;
